// Leaving the app for the sign-in app (admiral), and signing out.

import { getSessionConfig, ResolvedSessionConfig } from "./config";
import { cookies } from "./cookies";
import { endServerSession } from "./refresh";
import { invalidate } from "./requestCache";
import { currentUrlWithoutToken } from "./token";

/** localStorage key the apps read after sign-in to restore the page asked for */
export const RETURN_PATH_KEY = "from";

/**
 * Query parameters admiral reads from its own URL (AuthenticatedAppWrapper:
 * website cart hand-off, TMU provisioning). redirectToSignIn copies them,
 * when present, onto the admiral URL next to ?from= (as the per-app redirect
 * helpers did); they also stay inside `from`.
 */
export const FORWARDED_SIGN_IN_PARAMS = [
	"fromWebsite",
	"fromProvisionTMU",
	"doorlock",
	"immobilizer",
	"doorlock-immobilizer",
	"fob",
	"subscription",
	"monthly",
] as const;

const forwardedParams = (): string => {
	let current: URLSearchParams;
	try {
		current = new URLSearchParams(window.location.search);
	} catch {
		return "";
	}
	const forwarded = new URLSearchParams();
	FORWARDED_SIGN_IN_PARAMS.forEach((name) => {
		current.getAll(name).forEach((value) => forwarded.append(name, value));
	});
	const query = forwarded.toString();
	return query ? `&${query}` : "";
};

/**
 * Sends the visitor to `{admiralUrl}?from=<current URL without ?token=>`,
 * plus admiral's own parameters (FORWARDED_SIGN_IN_PARAMS) when present.
 * The sign-in app sends them back to that URL with a fresh ?token=. The page's
 * path is also kept in localStorage "from" (what the apps' post-login
 * navigation reads today).
 */
export function redirectToSignIn(reason?: string, config: ResolvedSessionConfig = getSessionConfig()): void {
	const path = window.location.pathname.endsWith("/")
		? window.location.pathname.slice(0, -1)
		: window.location.pathname;
	try {
		window.localStorage.setItem(RETURN_PATH_KEY, path || "");
	} catch {
		// storage unavailable: the from URL still carries the path
	}
	const target = `${config.admiralUrl}?from=${encodeURIComponent(currentUrlWithoutToken())}${forwardedParams()}`;
	if (reason) console.info(`[web-session:${config.app}] sign-in needed: ${reason}`);
	window.location.href = target;
}

export type SignOutOptions = {
	/** where to go afterwards: "signIn" (default), a URL, or false to stay */
	redirect?: "signIn" | string | false;
	config?: ResolvedSessionConfig;
};

/**
 * Ends the session everywhere this app can: POST /auth/logout (clears the
 * HttpOnly refresh cookie), the session cookies on the parent domain, the
 * request cache; then redirects (to sign-in by default).
 */
export async function signOut(options: SignOutOptions = {}): Promise<void> {
	const config = options.config ?? getSessionConfig();
	cookies.clearSession(config);
	invalidate();
	await endServerSession(config);
	const redirect = options.redirect ?? "signIn";
	if (redirect === false) return;
	if (redirect === "signIn") {
		redirectToSignIn("signed out", config);
		return;
	}
	window.location.href = redirect;
}
